import { useState, type KeyboardEvent } from 'react';
import { t } from '@bms2/shared';
import { Icon } from '../../components/Icon';
import chipStyles from '../../components/controls/Dropdown.module.css';
import styles from './settings.module.css';
import { removeLabel } from '../../components/removeLabel';

/**
 * A Settings value held as a string array, drawn as the tag row instead of a
 * textarea (piece 15, Blocked extensions): every value is a removable pill and
 * the box beneath them takes the next one. The values are free-typed, so there
 * is no registry to search and no Add New row — which is why this is not the
 * Chips control of piece 3, and why it borrows only that control's chip look.
 */
export interface ValueChipsProps {
  fieldKey: string;
  label: string;
  /** The values one per line, the shape every ValuesForm draft is kept in. */
  text: string;
  onChange: (next: string) => void;
}

/** A comma reads as a separator too, so a pasted list arrives as pills rather than one long pill. */
const SEPARATORS = /[,\n]/;

/** The values a draft holds, trimmed, in the order typed, each one once. */
export function splitValues(text: string): string[] {
  const out: string[] = [];
  for (const part of text.split(SEPARATORS)) {
    const value = part.trim();
    if (value && !out.includes(value)) out.push(value);
  }
  return out;
}

export function ValueChips({ fieldKey, label, text, onChange }: ValueChipsProps) {
  const [typed, setTyped] = useState('');
  const values = splitValues(text);

  const commit = (raw: string) => {
    setTyped('');
    const next = splitValues([text, raw].join('\n'));
    if (next.length !== values.length) onChange(next.join('\n'));
  };
  const remove = (value: string) => onChange(values.filter((v) => v !== value).join('\n'));
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(typed);
      return;
    }
    // Backspace in the empty box takes the last pill back, as the Chips control does.
    if (e.key === 'Backspace' && typed === '' && values.length > 0) {
      e.preventDefault();
      remove(values[values.length - 1]!);
    }
  };

  return (
    <div className={styles.chipField} data-settings-chips={fieldKey} data-chip-count={values.length}>
      <label className={styles.fieldLabel} htmlFor={`value-${fieldKey}`}>
        {label}
      </label>
      {values.length === 0 ? (
        <p className={styles.chipEmpty}>{t('settings.chips.none')}</p>
      ) : (
        <div className={chipStyles.chipRow} role="list" aria-label={label}>
          {values.map((value) => (
            <span key={value} className={chipStyles.chip} role="listitem" data-chip={value}>
              {value}
              <button
                type="button"
                className={chipStyles.chipRemove}
                aria-label={removeLabel('chips.remove', value)}
                onClick={() => remove(value)}
              >
                <Icon meaning="remove" size="inline" />
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Leaving the box commits what is in it: a value typed and then Saved is kept, not silently dropped. */}
      <input
        id={`value-${fieldKey}`}
        className={styles.chipInput}
        type="text"
        value={typed}
        placeholder={t('settings.chips.add')}
        onChange={(e) => setTyped(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(typed)}
        data-settings-field={fieldKey}
      />
    </div>
  );
}
